/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Play, Pause } from 'lucide-react';
import { NoteCollection } from '../api';
import { getIntervalColorForSkillLevel } from '../config/colorScheme';
import { useSkillLevel } from '../context/SkillLevelContext';
import { getIntervalLabel } from '../types/skillLevel';

interface IntervalsLegendProps {
  noteCollection: NoteCollection | null;
  currentKey: string;
  currentScale: string;
  isAnimating?: boolean;
  onToggleAnimation?: () => void;
}

const IntervalsLegend: React.FC<IntervalsLegendProps> = ({
  noteCollection,
  currentKey,
  currentScale,
  isAnimating = false,
  onToggleAnimation
}) => {
  const { features } = useSkillLevel();
  const useSimplifiedColors = features.useSimplifiedColors;
  const useSimplifiedLabels = features.useSimplifiedLegend;

  if (!noteCollection || !noteCollection.notesAndIntervals) {
    return null;
  }

  return (
    <Card className="intervals-legend mt-4">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex-1"></div>
          <h3 className="text-sm font-medium text-muted-foreground">
            {currentKey.toUpperCase()} {currentScale} - Intervals
          </h3>
          <div className="flex-1 flex justify-end">
            {onToggleAnimation && (
              <Button
                variant={isAnimating ? "default" : "outline"}
                size="sm"
                onClick={onToggleAnimation}
                className="h-7 w-7 p-0"
                title={isAnimating ? "Stop pattern animation" : "Animate scale pattern"}
              >
                {isAnimating ? (
                  <Pause className="h-3 w-3" />
                ) : (
                  <Play className="h-3 w-3" />
                )}
              </Button>
            )}
          </div>
        </div>
        
        <div className="flex flex-wrap justify-center gap-2">
          {noteCollection.notesAndIntervals.map((noteAndInterval, index) => (
            <div 
              key={index}
              className="flex items-center gap-2 px-3 py-1 rounded-full border"
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)' }}
            >
              <div
                className="w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold text-white border border-white"
                style={{
                  backgroundColor: getIntervalColorForSkillLevel(noteAndInterval.interval.abbreviation, useSimplifiedColors)
                }}
              >
                {noteAndInterval.note.note}
              </div>

              <div className="flex items-center gap-1">
                <span className="text-xs font-medium">
                  {noteAndInterval.note.note}
                </span>
                <span className="text-xs text-muted-foreground">
                  ({getIntervalLabel(noteAndInterval.interval.abbreviation, useSimplifiedLabels)})
                </span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default IntervalsLegend;